import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadTicketPdf } from "../lib/pdf";
import { ShieldCheck, CreditCard, CheckCircle, DownloadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState({});
  const [method, setMethod] = useState("mock");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/events/${eventId}`).then(({ data }) => {
      setEvent(data);
      const stored = sessionStorage.getItem("pending_checkout");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.event_id === eventId) setQty(parsed.qty || {});
      }
    });
  }, [eventId]);

  if (!event) return <div className="p-20 text-center" data-testid="checkout-loading">Loading…</div>;

  const items = event.ticket_types.map((t) => ({ ...t, qty: qty[t.id] || 0 })).filter((t) => t.qty > 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const fee = Math.round(subtotal * 0.05);
  const total = subtotal + fee;

  if (subtotal === 0 && !done) {
    return (
      <div className="max-w-2xl mx-auto p-20 text-center" data-testid="checkout-empty">
        <p className="font-display text-3xl">Your cart is empty</p>
        <Link to={`/events/${eventId}`} className="btn-red inline-block mt-6">Pick tickets</Link>
      </div>
    );
  }

  const pay = async () => {
    setBusy(true);
    try {
      if (method === "bkash") {
        const payload = {
          event_id: eventId,
          items: items.map((i) => ({ ticket_type_id: i.id, quantity: i.qty })),
        };
        const { data } = await api.post("/bkash/create", payload);
        if (data.bkash_url) {
          const callbackInfo = {
            order_id: data.order_id,
            payment_id: data.payment_id,
            event_id: eventId,
          };
          sessionStorage.setItem("bkash_pending", JSON.stringify(callbackInfo));
          window.location.href = data.bkash_url;
        } else {
          toast.error("bKash payment URL not received");
        }
        return;
      }
      const payload = {
        event_id: eventId,
        items: items.map((i) => ({ ticket_type_id: i.id, quantity: i.qty })),
        payment_method: method,
      };
      const { data } = await api.post("/checkout", payload);
      setDone(data);
      sessionStorage.removeItem("pending_checkout");
      toast.success("Payment successful! Tickets sent to your email.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="checkout-success">
        <div className="flex items-center gap-3 text-[#10B981]">
          <CheckCircle size={36} weight="fill" />
          <span className="label-eyebrow">Order confirmed</span>
        </div>
        <h1 className="font-display text-5xl mt-4 tracking-tighter">Enjoy the show.</h1>
        <p className="mt-4 text-neutral-600">A confirmation email has been sent to <strong>{user?.email}</strong>. Your tickets are in your dashboard.</p>

        <div className="mt-10 ticket-stub p-6 my-6 rounded-2xl shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <p className="label-eyebrow">Order #{done.order.id.slice(0, 8)}</p>
              <h2 className="font-display text-2xl mt-2">{done.order.event_title}</h2>
              <p className="text-sm text-neutral-600">{new Date(done.order.event_date).toLocaleString()}</p>
              <p className="text-sm text-neutral-600">{done.order.venue}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-neutral-500">Total</p>
              <p className="font-display text-2xl">৳{done.order.total.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {done.tickets.map((t) => (
            <div key={t.id} className="border border-neutral-200 p-4 flex items-center gap-4 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid={`ticket-${t.id}`}>
              <img src={t.qr_data_url} alt="QR" className="w-24 h-24 rounded-lg" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest font-bold">{t.ticket_type_name}</p>
                <p className="text-xs font-mono text-[#4F46E5] mt-1">{t.serial_number}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{t.gate}</p>
                <p className="font-display text-xl mt-2">৳{t.price.toLocaleString()}</p>
                <button
                  onClick={async () => {
                    const ok = await downloadTicketPdf(t.id, t.serial_number);
                    if (ok) toast.success("PDF downloaded"); else toast.error("Download failed");
                  }}
                  className="mt-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:text-[#4F46E5]"
                  data-testid={`download-pdf-${t.id}`}
                ><DownloadSimple size={14} weight="bold" /> Download PDF</button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-[#F8F9FA] border border-neutral-200 text-xs text-neutral-600 flex items-start gap-3 rounded-xl">
          <ShieldCheck size={16} className="text-[#4F46E5] mt-0.5" />
          <p>A confirmation email with all ticket PDFs has been sent to <strong>{user?.email}</strong>. Each ticket has a unique serial number and QR code that organizers will scan at the gate.</p>
        </div>

        <div className="mt-10 flex gap-3">
          <Link to="/dashboard" className="btn-red" data-testid="view-tickets-btn">View my tickets</Link>
          <Link to="/events" className="btn-outline">Browse more</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="checkout-page">
      <div className="lg:col-span-7">
        <span className="label-eyebrow">Step 02</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">Secure checkout.</h1>

        <div className="mt-10 border border-neutral-200 p-6 rounded-xl shadow-sm">
          <span className="label-eyebrow">Buyer</span>
          <p className="mt-2 font-bold">{user?.name}</p>
          <p className="text-sm text-neutral-600">{user?.email}</p>
        </div>

        <div className="mt-6 border border-neutral-200 p-6 rounded-xl shadow-sm">
          <span className="label-eyebrow">Payment Method</span>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: "mock", l: "Mock (Demo)", s: "Instant approval" },
              { k: "stripe", l: "Card / Stripe", s: "International" },
              { k: "bkash", l: "bKash", s: "Bangladesh" },
              { k: "sslcommerz", l: "SSLCommerz", s: "Bangladesh" },
            ].map((p) => (
              <button
                key={p.k}
                onClick={() => setMethod(p.k)}
                className={`p-4 border text-left rounded-xl transition-all ${method === p.k ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"}`}
                data-testid={`pay-${p.k}`}
              >
                <p className="font-bold flex items-center gap-2"><CreditCard size={16} /> {p.l}</p>
                <p className="text-xs opacity-70 mt-1">{p.s}</p>
              </button>
            ))}
          </div>
          {method === "bkash" && (
            <div className="mt-4 p-3 bg-[#E2136E]/5 border border-[#E2136E]/20 rounded-xl text-sm text-[#E2136E] flex items-start gap-2">
              <span className="font-bold">bKash</span>
              <span>You will be redirected to bKash to authorize the payment.</span>
            </div>
          )}
          <p className="text-xs text-neutral-500 mt-4 flex items-center gap-1">
            <ShieldCheck size={14} /> {method === "bkash" ? "bKash secure payment · Redirect to bKash app" : "Demo: all payments are mock-approved instantly."}
          </p>
        </div>
      </div>

      <aside className="lg:col-span-5">
        <div className="border border-black p-6 sticky top-24 rounded-2xl shadow-xl">
          <span className="label-eyebrow">Order Summary</span>
          <h3 className="font-display text-2xl mt-2">{event.title}</h3>
          <p className="text-sm text-neutral-600">{new Date(event.date).toLocaleString()}</p>
          <p className="text-sm text-neutral-600">{event.venue}, {event.city}</p>

          <div className="mt-4 divide-y divide-neutral-200">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between py-3">
                <span>{i.qty}× {i.name}</span>
                <span>৳{(i.price * i.qty).toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-neutral-300 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>৳{subtotal.toLocaleString()}</span></div>
            <div className="flex justify-between text-neutral-500"><span>Service fee (5%)</span><span>৳{fee.toLocaleString()}</span></div>
            <div className="flex justify-between font-display text-2xl pt-2 border-t border-neutral-300">
              <span>Total</span><span data-testid="checkout-total">৳{total.toLocaleString()}</span>
            </div>
          </div>

          <button onClick={pay} disabled={busy} className="btn-red w-full mt-6 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="pay-btn">
            {busy && <CircleNotch size={16} className="animate-spin" />}
            {busy ? "Processing…" : method === "bkash" ? `Pay ৳${total.toLocaleString()} via bKash` : `Pay ৳${total.toLocaleString()}`}
          </button>
          <p className="text-[11px] text-neutral-500 text-center mt-3">QR-secured tickets · Email & SMS confirmation · Fraud protection</p>
        </div>
      </aside>
    </div>
  );
}
